import { useEffect, useState } from 'react';
import { adminApi, formatDate } from '../api';

interface Incident {
  id: string;
  title: string;
  severity: string;
  status: string;
  components: string[];
  startedAt: string;
  resolvedAt: string | null;
}
const STATUSES = ['investigating', 'identified', 'monitoring', 'resolved'] as const;

/** Declare and update incidents shown on the public status page. Derived health still shows regardless. */
export function IncidentsSection({ tick }: { tick: number }) {
  const [list, setList] = useState<Incident[] | null>(null);
  const [components, setComponents] = useState<{ key: string; name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    severity: 'minor',
    status: 'investigating',
    body: '',
    components: [] as string[],
  });
  const [update, setUpdate] = useState<Record<string, { status: string; body: string }>>({});
  const load = () =>
    Promise.all([
      adminApi<{ incidents: Incident[] }>('/incidents').then((d) => setList(d.incidents)),
      fetch('/api/public/status')
        .then((r) => r.json())
        .then((d) => setComponents((d as { components: { key: string; name: string }[] }).components)),
    ]).catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [tick]);
  return (
    <div className="card">
      <div className="row between">
        <h2>Status page incidents</h2>
        <a href="/status" target="_blank" rel="noreferrer" className="muted" style={{ fontSize: 12 }}>
          open status page
        </a>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="grid cols-2">
        <div>
          <h3 style={{ fontSize: 14 }}>Open an incident</h3>
          <div className="field">
            <label>Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Elevated connection errors in Texas"
            />
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label>Severity</label>
              <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                <option value="minor">minor (degraded)</option>
                <option value="major">major (outage)</option>
                <option value="maintenance">maintenance</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Affected</label>
            {components.map((c) => (
              <label
                key={c.key}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  color: 'var(--text)',
                  fontWeight: 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={form.components.includes(c.key)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      components: e.target.checked
                        ? [...form.components, c.key]
                        : form.components.filter((k) => k !== c.key),
                    })
                  }
                />
                {c.name}
              </label>
            ))}
          </div>
          <div className="field">
            <label>First update (what customers see)</label>
            <textarea
              rows={3}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </div>
          <button
            disabled={!form.title || !form.body || form.components.length === 0}
            onClick={() =>
              adminApi('/incidents', { method: 'POST', json: form })
                .then(() => {
                  setForm({
                    title: '',
                    severity: 'minor',
                    status: 'investigating',
                    body: '',
                    components: [],
                  });
                  return load();
                })
                .catch((e: Error) => setError(e.message))
            }
          >
            Publish
          </button>
        </div>
        <div>
          <h3 style={{ fontSize: 14 }}>Incidents</h3>
          {list?.length === 0 && <p className="muted">None yet.</p>}
          {list?.map((inc) => (
            <div key={inc.id} className="msg" style={{ marginBottom: 10 }}>
              <div className="row between">
                <strong>{inc.title}</strong>
                <span className={`badge ${inc.status === 'resolved' ? 'active' : 'provisioning'}`}>
                  {inc.status}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                {inc.severity} · {formatDate(inc.startedAt)} · {inc.components.join(', ')}
              </div>
              {inc.status !== 'resolved' && (
                <div className="row" style={{ marginTop: 8, alignItems: 'flex-start' }}>
                  <select
                    style={{ width: 150 }}
                    value={update[inc.id]?.status ?? inc.status}
                    onChange={(e) =>
                      setUpdate({
                        ...update,
                        [inc.id]: { status: e.target.value, body: update[inc.id]?.body ?? '' },
                      })
                    }
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <input
                    placeholder="Update text"
                    value={update[inc.id]?.body ?? ''}
                    onChange={(e) =>
                      setUpdate({
                        ...update,
                        [inc.id]: { status: update[inc.id]?.status ?? inc.status, body: e.target.value },
                      })
                    }
                  />
                  <button
                    className="secondary"
                    disabled={!update[inc.id]?.body}
                    onClick={() =>
                      adminApi(`/incidents/${inc.id}/updates`, { method: 'POST', json: update[inc.id] })
                        .then(() => {
                          setUpdate({ ...update, [inc.id]: { status: 'investigating', body: '' } });
                          return load();
                        })
                        .catch((e: Error) => setError(e.message))
                    }
                  >
                    Post
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
